import { Command as CommandPrimitive } from 'cmdk';
import { Dialog as DialogPrimitive, VisuallyHidden } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Icon, type IconName } from './icon';
import { Spinner } from './spinner';

/**
 * Building blocks of the command palette and the global search (Ctrl/⌘ K). Full screen on the
 * phone, a centered panel elsewhere.
 */
export function CommandDialog({
  open,
  onOpenChange,
  label,
  children,
  shouldFilter = true,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Accessible name of the dialog, e.g. "Suche". */
  label: string;
  children: ReactNode;
  /** Off when the results come filtered from the server. */
  shouldFilter?: boolean;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 flex justify-center bg-scrim data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in md:items-start md:px-6 md:pt-[12vh]">
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="flex h-dvh w-full flex-col overflow-hidden bg-raised shadow-overlay outline-none data-[state=open]:animate-pop-in md:h-auto md:max-h-[70vh] md:max-w-xl md:rounded-lg md:border md:border-line"
          >
            <VisuallyHidden.Root>
              <DialogPrimitive.Title>{label}</DialogPrimitive.Title>
            </VisuallyHidden.Root>
            <CommandPrimitive
              label={label}
              shouldFilter={shouldFilter}
              loop
              className="flex min-h-0 flex-1 flex-col"
            >
              {children}
            </CommandPrimitive>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function CommandInput({
  className,
  trailing,
  ...props
}: ComponentProps<typeof CommandPrimitive.Input> & { trailing?: ReactNode }) {
  return (
    <div className="flex items-center gap-2 border-b border-line px-4">
      <Icon name="search" className="size-[1.125rem] text-fg-subtle" />
      <CommandPrimitive.Input
        className={cn(
          'h-14 min-w-0 flex-1 bg-transparent text-base text-fg outline-none placeholder:text-fg-subtle',
          className,
        )}
        {...props}
      />
      {trailing}
    </div>
  );
}

export function CommandList({ className, ...props }: ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain p-2', className)}
      {...props}
    />
  );
}

export function CommandEmpty({ children }: { children: ReactNode }) {
  return (
    <CommandPrimitive.Empty className="px-4 py-10 text-center text-fg-muted">
      {children}
    </CommandPrimitive.Empty>
  );
}

export function CommandLoading({ label }: { label: string }) {
  return (
    <CommandPrimitive.Loading>
      <div className="flex items-center gap-2 px-3 py-3 text-fg-muted">
        <Spinner />
        {label}
      </div>
    </CommandPrimitive.Loading>
  );
}

export function CommandGroup({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      className={cn(
        'pb-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-fg-subtle',
        className,
      )}
      {...props}
    />
  );
}

export interface CommandItemProps extends ComponentProps<typeof CommandPrimitive.Item> {
  icon?: IconName;
  /** Module accent of the hit, so its origin is visible at a glance (design-system.md). */
  accent?: string;
  description?: ReactNode;
  /** Right side, e.g. the module name or a shortcut. */
  hint?: ReactNode;
}

export function CommandItem({
  icon,
  accent,
  description,
  hint,
  className,
  children,
  ...props
}: CommandItemProps) {
  return (
    <CommandPrimitive.Item
      data-module-accent={accent}
      className={cn(
        'flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-fg outline-none select-none data-[disabled=true]:cursor-not-allowed data-[disabled=true]:text-fg-disabled data-[selected=true]:bg-hover',
        className,
      )}
      {...props}
    >
      {icon && (
        <span className="flex size-7 shrink-0 items-center justify-center rounded-sm bg-accent-subtle text-accent">
          <Icon name={icon} />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{children}</span>
        {description && <span className="block truncate text-xs text-fg-muted">{description}</span>}
      </span>
      {hint && <span className="shrink-0 text-xs text-fg-subtle">{hint}</span>}
    </CommandPrimitive.Item>
  );
}

export function CommandSeparator() {
  return <CommandPrimitive.Separator className="-mx-2 my-1 h-px bg-line" />;
}

/** Bottom line with keyboard hints. Hidden on touch devices. */
export function CommandFooter({ children }: { children: ReactNode }) {
  return (
    <div className="hidden items-center gap-4 border-t border-line bg-canvas px-4 py-2 text-xs text-fg-subtle md:flex [@media(pointer:coarse)]:hidden">
      {children}
    </div>
  );
}
